"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Plus, Zap, Hand } from "lucide-react"
import type { DocumentClassifier, DocumentEvent, Integration } from "@/types"
import { ActionCard } from "./action-card"
import { ActionDialog } from "./action-dialog"

interface EventTriggersSectionProps {
  classification: DocumentClassifier
  automaticEvents: DocumentEvent[]
  manualEvents: DocumentEvent[]
  allDocumentEvents: DocumentEvent[]
  integrations: Integration[]
  onUpdateDocumentEvents: (events: DocumentEvent[]) => void
}

export function EventTriggersSection({
  classification,
  automaticEvents,
  manualEvents,
  allDocumentEvents,
  integrations,
  onUpdateDocumentEvents,
}: EventTriggersSectionProps) {
  const [autoDialogOpen, setAutoDialogOpen] = useState(false)
  const [manualDialogOpen, setManualDialogOpen] = useState(false)
  const [editingAuto, setEditingAuto] = useState<DocumentEvent | null>(null)
  const [editingManual, setEditingManual] = useState<DocumentEvent | null>(null)

  return (
    <div className="space-y-6">
      {/* Automatic Actions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />
            <h4 className="font-medium">Automatic</h4>
          </div>
          <ActionDialog
            open={autoDialogOpen}
            onOpenChange={setAutoDialogOpen}
            classification={classification}
			dc_id={classification.id}
            editing={editingAuto}
            auto={true}
            allEvents={allDocumentEvents}
            integrations={integrations}
            onUpdate={onUpdateDocumentEvents}
            onEditingChange={setEditingAuto}
            trigger={
              <Button size="sm" variant="outline" onClick={() => setEditingAuto(null)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Automatic Action
              </Button>
            }
          />
        </div>
        <div className="space-y-2">
          {automaticEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No automatic actions configured</p>
          ) : (
            automaticEvents.map((event) => (
              <ActionCard
                key={event.id}
                event={event}
                isAutomatic={true}
                onEdit={() => {
                  setEditingAuto(event)
                  setAutoDialogOpen(true)
                }}
                onDelete={() => onUpdateDocumentEvents(allDocumentEvents.filter((e) => e.id !== event.id))}
              />
            ))
          )}
        </div>
      </div>

      {/* Manual Actions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hand className="h-4 w-4 text-primary" />
            <h4 className="font-medium">Manual</h4>
          </div>
          <ActionDialog
            open={manualDialogOpen}
            onOpenChange={setManualDialogOpen}
            classification={classification}
            editing={editingManual}
            auto={false}
            allEvents={allDocumentEvents}
            integrations={integrations}
            onUpdate={onUpdateDocumentEvents}
            onEditingChange={setEditingManual}
            trigger={
              <Button size="sm" variant="outline" onClick={() => setEditingManual(null)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Manual Action
              </Button>
            }
          />
        </div>
        <div className="space-y-2">
          {manualEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No manual actions configured</p>
          ) : (
            manualEvents.map((event) => (
              <ActionCard
                key={event.id}
                event={event}
                isAutomatic={false}
                onEdit={() => {
                  setEditingManual(event)
                  setManualDialogOpen(true)
                }}
                onDelete={() => onUpdateDocumentEvents(allDocumentEvents.filter((e) => e.id !== event.id))}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

